import React from 'react';
import { motion } from 'framer-motion';
import { WaveLogo } from '../shared';
import { ONBOARDING_STEPS } from './data';

const GREEN = '#00B061';
const ORANGE = '#f08416';
const DIM_LINE = '#383838';
const PENDING = '#5a5a5a';
const MONO = 'ui-monospace,SFMono-Regular,Menlo,Consolas,monospace';
const LINE_DUR = 0.4;
const POST_DELAY = 0.28;

const HBubble: React.FC<{
  num: string;
  index: number;
  step: number;
  dot: number;
}> = ({ num, index, step, dot }) => {
  const done = index < step;
  const isActive = index === step;
  const accent = done ? GREEN : isActive ? ORANGE : PENDING;
  const transition =
    `background-color .35s ease ${POST_DELAY}s,` +
    ` border-color .35s ease ${POST_DELAY}s,` +
    ` color .35s ease ${POST_DELAY}s, box-shadow .35s ease ${POST_DELAY}s`;
  return (
    <div
      style={{ position: 'relative', width: dot, height: dot, flexShrink: 0 }}
    >
      <div
        style={{
          boxSizing: 'border-box',
          position: 'relative',
          zIndex: 1,
          width: dot,
          height: dot,
          borderRadius: dot,
          background: done ? GREEN : '#000',
          border: `2px solid ${accent}`,
          boxShadow: isActive ? '0 0 12px rgba(240,132,22,.7)' : 'none',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: done ? '#000' : accent,
          fontFamily: MONO,
          fontSize: 11,
          fontWeight: 700,
          transition,
        }}
      >
        {done ? (
          <svg
            width={Math.round(dot * 0.55)}
            height={Math.round(dot * 0.55)}
            viewBox="0 0 16 16"
            style={{ display: 'block' }}
          >
            <path
              d="M3 8 L7 12 L13 4"
              fill="none"
              stroke="#000"
              strokeWidth={2}
            />
          </svg>
        ) : (
          num
        )}
      </div>
      {isActive && (
        <motion.span
          key={`hr-${step}`}
          initial={{ scale: 1, opacity: 0.75 }}
          animate={{ scale: 2.2, opacity: 0 }}
          transition={{ duration: 0.7, ease: 'easeOut', delay: POST_DELAY }}
          style={{
            position: 'absolute',
            inset: 0,
            zIndex: 0,
            borderRadius: dot,
            border: `2px solid ${ORANGE}`,
            pointerEvents: 'none',
          }}
        />
      )}
    </div>
  );
};

const HLine: React.FC<{ index: number; step: number }> = ({ index, step }) => {
  const lineColor =
    index + 1 === step ? ORANGE : index + 1 < step ? GREEN : DIM_LINE;
  const isColored = lineColor !== DIM_LINE;
  return (
    <div
      style={{
        flex: 1,
        height: 2,
        margin: '0 6px',
        minWidth: 8,
        position: 'relative',
        background: DIM_LINE,
      }}
    >
      {/* Origin-left scaleX: fills left→right forward, recedes going back. */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background: isColored ? lineColor : 'transparent',
          transformOrigin: 'left',
          transform: `scaleX(${isColored ? 1 : 0})`,
          transition: `transform ${LINE_DUR}s ease, background-color .3s ease`,
        }}
      />
    </div>
  );
};

// Compact top stepper used on mobile (and the tablet "top" layout).
const HorizontalRail: React.FC<{ step: number; phone?: boolean }> = ({
  step,
  phone,
}) => {
  const dot = phone ? 26 : 30;
  const meta = ONBOARDING_STEPS[Math.min(step, 4)];
  const done = step > 4;
  return (
    <div
      style={{
        flexShrink: 0,
        boxSizing: 'border-box',
        padding: phone ? '18px 20px' : '22px 32px',
        borderBottom: '2px double rgba(255,255,255,.5)',
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span
          style={{
            width: 11,
            height: 11,
            background: GREEN,
            boxShadow: '0 0 10px rgba(0,176,97,.7)',
            borderRadius: 1,
            transform: 'rotate(45deg)',
            flexShrink: 0,
          }}
        />
        <WaveLogo size={18} />
      </div>

      <div style={{ display: 'flex', alignItems: 'center' }}>
        {ONBOARDING_STEPS.map((s, i) => (
          <React.Fragment key={s.n}>
            <HBubble num={s.n} index={i} step={step} dot={dot} />
            {i < ONBOARDING_STEPS.length - 1 && <HLine index={i} step={step} />}
          </React.Fragment>
        ))}
      </div>

      <div
        style={{
          fontFamily: 'Bebas Neue, Kanit',
          fontSize: phone ? 26 : 30,
          letterSpacing: '.03em',
          lineHeight: 1,
          color: done ? '#b2afaf' : ORANGE,
          transition: 'color .35s ease',
        }}
      >
        {meta.title}
      </div>
    </div>
  );
};

export default HorizontalRail;
